import { createStore, createEffect, createEvent, combine, sample } from 'effector';
import i18n from 'i18n';
import { $notificationStore } from '../notificationStore';
import { ApiUtils, FileUtils } from '@shared/utils';
import { MessagesApi } from '@shared/api/rest';
import { type IQueryParams, RequestStrategyEnum, type ApiResponse, type FileResponse } from '@shared/api/models';
import type {
  AddMessageResponse,
  AddMessageWithAttachmentRequest,
  DownloadAttachmentRequest,
  ReadThreadsRequest,
  ThreadsResponse,
  ThreadWithMessages,
} from '@shared/api/rest/messages';

const getInitialThreadsEffect = createEffect((params: IQueryParams) => MessagesApi.getThreads(params?.signal));
const getInitialThreadsWithMessages = createEffect(async (data: ApiResponse<ThreadsResponse[]>) => {
  return await Promise.all(
    data.data?.map(async (thread) => {
      const threadMessages = (await MessagesApi.getThreadsMessages(thread.groupId)).data;
      return { ...thread, messages: threadMessages };
    }) || [],
  );
});
const getThreadsEffect = createEffect(() => MessagesApi.getThreads());
const getThreadsWithMessages = createEffect(async (data: ApiResponse<ThreadsResponse[]>) => {
  return await Promise.all(
    data.data?.map(async (thread) => {
      const threadMessages = (await MessagesApi.getThreadsMessages(thread.groupId)).data;
      return { ...thread, messages: threadMessages };
    }) || [],
  );
});
const deleteThreadEffect = createEffect(async (groupId: string) => {
  await MessagesApi.deleteThread(groupId);
  return groupId;
});
const addAttachmentForNewThreadEffect = createEffect(
  async (data: { file: FormData | undefined; data: AddMessageResponse | undefined }) =>
    await MessagesApi.addAttachments({ request: data.file!, messageId: data.data!.messageId }),
);
const createNewThreadWithMessageEffect = createEffect(async (request: AddMessageWithAttachmentRequest) => {
  const { subject, body, file } = request;
  const response = await MessagesApi.addMessage({ subject, body });
  return { file, data: response.data };
});
const getThreadMessages = createEffect(async ({ groupId }: { groupId: string | undefined }) => {
  const response = await MessagesApi.getThreadsMessages(groupId!);
  return { groupId, data: response.data };
});
const addAttachmentForNewMessageEffect = createEffect(
  async (data: { file: FormData | undefined; data: AddMessageResponse | undefined; groupId?: string }) => {
    await MessagesApi.addAttachments({ request: data.file!, messageId: data.data!.messageId });
    return { groupId: data.groupId };
  },
);
const addNewMessageToThreadEffect = createEffect(async (params: { request: AddMessageWithAttachmentRequest }) => {
  const { groupId, subject, body, file } = params.request;

  const response = await MessagesApi.addMessage({ groupId, subject, body });
  return { groupId, file, data: response.data };
});

const readThread = createEffect(async (params: { request: ReadThreadsRequest }) => {
  const { groupId } = params.request;

  const response = await MessagesApi.readThread({ groupId });
  return { groupId, data: response.data };
});

const getAttachmentEffect = createEffect(async (params: { request: DownloadAttachmentRequest }) => MessagesApi.downloadAttachment(params.request));
const downloadAttachmentEffect = createEffect(async (params: { data: FileResponse }) => FileUtils.downloadFile(params.data));
const clearEvent = createEvent();

sample({
  source: getInitialThreadsEffect.doneData,
  target: getInitialThreadsWithMessages,
});
sample({
  source: getThreadsEffect.doneData,
  target: getThreadsWithMessages,
});
sample({
  source: createNewThreadWithMessageEffect.doneData,
  filter: (data) => !!data.file && !!data.data,
  target: addAttachmentForNewThreadEffect,
});
sample({
  source: createNewThreadWithMessageEffect.doneData,
  filter: (data) => !data.file,
  target: getThreadsEffect,
});
sample({
  source: addAttachmentForNewThreadEffect.doneData,
  target: getThreadsEffect,
});
sample({
  source: addNewMessageToThreadEffect.doneData,
  filter: (data) => !!data.file && !!data.data,
  target: addAttachmentForNewMessageEffect,
});
sample({
  source: addNewMessageToThreadEffect.doneData,
  filter: (data) => !data.file && !!data.groupId,
  target: getThreadMessages,
});
sample({
  source: addAttachmentForNewMessageEffect.doneData,
  target: getThreadMessages,
});
sample({
  source: getAttachmentEffect.doneData,
  filter: (data) => !!data,
  target: downloadAttachmentEffect.prepend((data: FileResponse) => ({ data })),
});

const $dataStore = createStore<ThreadWithMessages[]>([])
  .on(getInitialThreadsWithMessages.done, (current, data) => data.result ?? current)
  .on(getThreadsWithMessages.done, (current, data) => data.result ?? current)
  .on(getThreadMessages.done, (current, data) =>
    current
      .map((thread) => {
        if (thread.groupId === data.result.groupId) {
          return {
            ...thread,
            messages: data.result.data ? data.result.data : thread.messages,
          };
        }
        return thread;
      })
      .sort((a) => (a.groupId === data.result.groupId ? -1 : 1)),
  )
  .on(deleteThreadEffect.done, (current, data) => current.filter((thread) => thread.groupId !== data.result))
  .reset(clearEvent);

const $initialLoadingStore = createStore<boolean>(false)
  .on(getInitialThreadsEffect.pending, (_, data) => data)
  .on(getInitialThreadsEffect.fail, () => false)
  .on(getInitialThreadsWithMessages.pending, (_, data) => data)
  .on(getInitialThreadsWithMessages.fail, () => false)
  .on(getInitialThreadsWithMessages.done, () => false)
  .reset(clearEvent);
const $loading = createStore<boolean>(false)
  .on(getThreadsEffect.pending, (_, data) => data)
  .on(getThreadsEffect.fail, () => false)
  .on(getThreadsWithMessages.pending, (_, data) => data)
  .on(getThreadsWithMessages.fail, () => false)
  .on(getThreadsWithMessages.done, () => false)
  .on(getThreadMessages.pending, (_, data) => data)
  .on(getThreadMessages.fail, () => false)
  .on(getThreadMessages.done, () => false)
  .on(createNewThreadWithMessageEffect.pending, (_, data) => data)
  .on(createNewThreadWithMessageEffect.fail, () => false)
  .on(addAttachmentForNewThreadEffect.pending, (_, data) => data)
  .on(addAttachmentForNewThreadEffect.fail, () => false)
  .on(addNewMessageToThreadEffect.pending, (_, data) => data)
  .on(addNewMessageToThreadEffect.fail, () => false)
  .on(addAttachmentForNewMessageEffect.pending, (_, data) => data)
  .on(addAttachmentForNewMessageEffect.fail, () => false)
  .on(deleteThreadEffect.pending, (_, data) => data)
  .on(deleteThreadEffect.fail, () => false)
  .on(deleteThreadEffect.done, () => false)
  .on(getAttachmentEffect.pending, (_, data) => data)
  .on(getAttachmentEffect.fail, () => false)
  .on(downloadAttachmentEffect.pending, (_, data) => data)
  .on(downloadAttachmentEffect.fail, () => false)
  .on(downloadAttachmentEffect.done, () => false)
  // .on(readThread.pending, (_, data) => data)
  // .on(readThread.fail, () => false)
  // .on(readThread.done, () => false)
  .reset(clearEvent);

const $errorStore = createStore<string | null>(null)
  .on(getInitialThreadsEffect, () => null)
  .on(getInitialThreadsEffect.fail, (_, data) => i18n.t(`errors.api.${data.error.message}`, data.error.message))
  .on(getInitialThreadsEffect.done, () => null)
  .on(getInitialThreadsWithMessages, () => null)
  .on(getInitialThreadsWithMessages.fail, (_, data) => i18n.t(`errors.api.${data.error.message}`, data.error.message))
  .on(getInitialThreadsWithMessages.done, () => null)
  .on(getThreadsEffect, () => null)
  .on(getThreadsEffect.fail, (_, data) => i18n.t(`errors.api.${data.error.message}`, data.error.message))
  .on(getThreadsEffect.done, () => null)
  .on(getThreadsWithMessages, () => null)
  .on(getThreadsWithMessages.fail, (_, data) => i18n.t(`errors.api.${data.error.message}`, data.error.message))
  .on(getThreadsWithMessages.done, () => null)
  .on(deleteThreadEffect, () => null)
  .on(deleteThreadEffect.fail, (_, data) => i18n.t(`errors.api.${data.error.message}`, data.error.message))
  .on(deleteThreadEffect.done, () => null)
  .on(addAttachmentForNewThreadEffect, () => null)
  .on(addAttachmentForNewThreadEffect.fail, (_, data) => i18n.t(`errors.api.${data.error.message}`, data.error.message))
  .on(addAttachmentForNewThreadEffect.done, () => null)
  .on(createNewThreadWithMessageEffect, () => null)
  .on(createNewThreadWithMessageEffect.fail, (_, data) => i18n.t(`errors.api.${data.error.message}`, data.error.message))
  .on(createNewThreadWithMessageEffect.done, () => null)
  .on(getThreadMessages, () => null)
  .on(getThreadMessages.fail, (_, data) => i18n.t(`errors.api.${data.error.message}`, data.error.message))
  .on(getThreadMessages.done, () => null)
  .on(addAttachmentForNewMessageEffect, () => null)
  .on(addAttachmentForNewMessageEffect.fail, (_, data) => i18n.t(`errors.api.${data.error.message}`, data.error.message))
  .on(addAttachmentForNewMessageEffect.done, () => null)
  .on(addNewMessageToThreadEffect, () => null)
  .on(addNewMessageToThreadEffect.fail, (_, data) => i18n.t(`errors.api.${data.error.message}`, data.error.message))
  .on(addNewMessageToThreadEffect.done, () => null)
  .on(getAttachmentEffect, () => null)
  .on(getAttachmentEffect.fail, (_, data) => i18n.t(`errors.api.${data.error.message}`, data.error.message))
  .on(getAttachmentEffect.done, () => null)
  .on(downloadAttachmentEffect, () => null)
  .on(downloadAttachmentEffect.fail, (_, data) => i18n.t(`errors.api.${data.error.message}`, data.error.message))
  .on(downloadAttachmentEffect.done, () => null)
  .on(readThread, () => null)
  .on(readThread.fail, (_, data) => i18n.t(`errors.api.${data.error.message}`, data.error.message))
  .on(readThread.done, () => null)
  .reset(clearEvent);
$errorStore.watch((x) => $notificationStore.events.setError(x));

export const $messageCentreStore = {
  events: {
    getThreads: ApiUtils.wrapperApi(getInitialThreadsEffect, RequestStrategyEnum.Debounce),
    createNewThreadWithMessage: createNewThreadWithMessageEffect,
    addNewMessageToThread: ApiUtils.wrapperApi(addNewMessageToThreadEffect, RequestStrategyEnum.Debounce),
    deleteThread: deleteThreadEffect,
    downloadAttachment: ApiUtils.wrapperApi(getAttachmentEffect, RequestStrategyEnum.Debounce),
    readThread: ApiUtils.wrapperApi(readThread, RequestStrategyEnum.Debounce),
    clear: clearEvent,
  },
  store: combine({
    data: $dataStore,
    firstLoading: $initialLoadingStore,
    loading: $loading,
    error: $errorStore,
  }),
};
